import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getTeamReport } from "../../services/reportService";
import { 
  FaEye, 
  FaUsers, 
  FaEnvelope, 
  FaPhone, 
  FaChartLine,
  FaSearch,
  FaChevronLeft,
  FaChevronRight,
  FaUserTie,
  FaCalendarAlt,
  FaBuilding
} from "react-icons/fa";
import "./TeamReport.css";

const ITEMS_PER_PAGE = 5;

const TeamReportLayer = () => {
  const [tmList, setTmList] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [summary, setSummary] = useState({
    totalTMs: 0,
    totalSales: 0,
    avgSalesPerTM: 0
  });

  const navigate = useNavigate();

  useEffect(() => {
    loadReport();
  }, []);

  useEffect(() => {
    applyFilter();
  }, [search, tmList]);

  const loadReport = async () => {
    setLoading(true);
    const data = await getTeamReport();
    const tmReport = data?.tm_report || [];
    setTmList(tmReport);
    
    // Calculate summary
    const totalTMs = tmReport.length;
    const totalSales = tmReport.reduce((sum, tm) => sum + (tm.sales_count || 0), 0);
    const avgSalesPerTM = totalTMs > 0 ? (totalSales / totalTMs).toFixed(1) : 0;
    
    setSummary({
      totalTMs,
      totalSales,
      avgSalesPerTM
    });
    setLoading(false);
  };

  const applyFilter = () => {
    const f = tmList.filter((tm) =>
      tm.tm_name?.toLowerCase().includes(search.toLowerCase()) ||
      tm.email?.toLowerCase().includes(search.toLowerCase()) ||
      tm.mobile?.includes(search)
    );
    setFiltered(f);
    setPage(1);
  };

  const start = (page - 1) * ITEMS_PER_PAGE;
  const paginated = filtered.slice(start, start + ITEMS_PER_PAGE);
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);

  return (
    <div className="team-report-container">
      {/* Header Section */}
      <div className="team-header">
        <div className="header-title">
          <FaUserTie className="header-icon" />
          <div>
            <h2>Team Performance Report</h2>
            <p>Manage and monitor your team managers' performance</p>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <div className="card-icon tm-icon">
            <FaUsers />
          </div>
          <div className="card-content">
            <span className="card-label">Total Team Managers</span>
            <span className="card-value">{summary.totalTMs}</span>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon sales-icon">
            <FaChartLine />
          </div>
          <div className="card-content">
            <span className="card-label">Total Sales Executives</span>
            <span className="card-value">{summary.totalSales}</span>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon avg-icon">
            <FaBuilding />
          </div>
          <div className="card-content">
            <span className="card-label">Avg Sales per TM</span>
            <span className="card-value">{summary.avgSalesPerTM}</span>
          </div>
        </div>
      </div>

      {/* Main Card */}
      <div className="team-card">
        <div className="card-header">
          <div className="header-left">
            <h5>Team Managers List</h5>
            <span className="record-badge">{filtered.length} records found</span>
          </div>
          <div className="search-wrapper">
            <FaSearch className="search-icon" />
            <input
              className="search-input"
              placeholder="Search by name, email or mobile..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading team data...</p>
          </div>
        ) : (
          <>
            <div className="table-wrap">
              <table className="team-table">
                <thead>
                  <tr>
                    <th>Team Manager</th>
                    <th>Contact Information</th>
                    <th>Sales Team</th>
                    <th className="action-col">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="empty-state">
                        <FaUsers className="empty-icon" />
                        <p>No team managers found</p>
                        <small>Try adjusting your search</small>
                      </td>
                    </tr>
                  ) : (
                    paginated.map((tm) => (
                      <tr key={tm.tm_id}>
                        <td>
                          <div className="tm-info">
                            <div className="tm-avatar">
                              <FaUserTie />
                            </div>
                            <div className="tm-details">
                              <strong>{tm.tm_name}</strong>
                              <small>ID: {tm.tm_id}</small>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="contact-info">
                            <span><FaEnvelope /> {tm.email}</span>
                            <span><FaPhone /> {tm.mobile}</span>
                          </div>
                        </td>
                        <td>
                          <div className="sales-badge">
                            <FaUsers className="badge-icon" />
                            <span className="sales-count">{tm.sales_count || 0}</span>
                            <span className="sales-label">Sales Executives</span>
                          </div>
                        </td>
                        <td className="action-cell">
                          <button
                            className="view-btn"
                            onClick={() => navigate(`/tm-report/${tm.tm_id}`)}
                            title="View Team Details"
                          >
                            <FaEye />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  className="page-btn"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  <FaChevronLeft /> Prev
                </button>
                <div className="page-info">
                  <span className="current-page">{page}</span>
                  <span className="total-pages">of {totalPages}</span>
                </div>
                <button
                  className="page-btn"
                  disabled={page === totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next <FaChevronRight />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TeamReportLayer;